/**
 * DestinationAutocomplete Komponenti
 * 
 * BlogSearchDropdown benzeri performanslı autocomplete
 * Cache, debounce, abort controller desteği
 */

'use client'

import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext'
import { transformMediaUrl } from '@/lib/media-url-transformer'
import type { Suggestion } from '@/types/product-search'

export interface DestinationAutocompleteProps {
    fieldKey: string
    label: string
    icon?: React.ReactNode
    placeholder?: string
    value?: string
    onChange?: (value: string) => void
    onSelect?: (suggestion: Suggestion | null) => void
    getSuggestions: (query: string) => Promise<Suggestion[]>
    isLoading?: boolean
    isRequired?: boolean
    autoOpenDropdown?: boolean // SidebarFilters için false, MainSearchBar için true (default)
}

const MIN_SEARCH_LENGTH = 3 // Minimum 3 karakter gerekli
const DEBOUNCE_DELAY = 300
const CACHE_TTL = 5 * 60 * 1000 // 5 dakika
const MAX_CACHE_SIZE = 50

const highlightText = (text: string, searchTerm: string): React.ReactNode => {
    if (!searchTerm || !text) {
        return text
    }

    const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(${escapedTerm})`, 'gi')
    const parts = text.split(regex)

    return parts.map((part, index) => {
        if (part.toLowerCase() === searchTerm.toLowerCase()) {
            return (
                <mark
                    key={index}
                    className="bg-alsero-first/25 text-alsero-first font-bold px-1 py-0.5 rounded-sm"
                >
                    {part}
                </mark>
            )
        }
        return <span key={index}>{part}</span>
    })
}

export default function DestinationAutocomplete({
    fieldKey,
    label,
    icon,
    placeholder,
    value = '',
    onChange,
    onSelect,
    getSuggestions,
    isLoading: externalLoading = false,
    isRequired = false,
    autoOpenDropdown = true, // Default: true (MainSearchBar için), SidebarFilters için false
}: DestinationAutocompleteProps) {
    const [query, setQuery] = useState(value)
    const [suggestions, setSuggestions] = useState<Suggestion[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState(-1)
    const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [isSearchingState, setIsSearchingState] = useState(false)
    
    const inputRef = useRef<HTMLInputElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const inputRowRef = useRef<HTMLDivElement>(null)
    const dropdownPanelRef = useRef<HTMLDivElement>(null)
    const [dropdownPos, setDropdownPos] = useState<{
        top: number
        left: number
        width: number
        maxHeight: number
    } | null>(null)
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const abortControllerRef = useRef<AbortController | null>(null)
    const cacheRef = useRef<Map<string, { data: Suggestion[]; timestamp: number }>>(new Map())
    const pendingRequestsRef = useRef<Map<string, Promise<Suggestion[]>>>(new Map())
    const isSearchingRef = useRef<boolean>(false)
    const isInitialLoadRef = useRef<boolean>(true)
    const previousValueRef = useRef<string>('')
    const prefersReduced = useReducedMotion()
    const { getVariable } = useLanguageVariables()

    const displayValue = useMemo(() => {
        return selectedSuggestion ? selectedSuggestion.name : query
    }, [selectedSuggestion, query])

    // Trimmed query - memoize edilmiş
    const trimmedQuery = useMemo(() => query.trim(), [query])

    const closeDropdown = useCallback(() => {
        setShowSuggestions(false)
        isSearchingRef.current = false
        setIsLoading(false)
    }, [])

    const stopSearch = useCallback(() => {
        isSearchingRef.current = false
        setIsSearchingState(false)
        closeDropdown()
    }, [closeDropdown])

    const updatePreviousValue = useCallback((value: string) => {
        previousValueRef.current = value
    }, [])

    const isInitialLoad = useCallback(() => isInitialLoadRef.current, [])

    const resetToValue = useCallback((value: string) => {
        setQuery(value)
        setSelectedSuggestion(null)
        stopSearch()
        updatePreviousValue(value)
    }, [stopSearch, updatePreviousValue])

    // Cache temizleme fonksiyonu - ayrı fonksiyon olarak optimize edildi
    const cleanupCache = useCallback(() => {
        const now = Date.now()
        const entriesToDelete: string[] = []
        
        // TTL'den geçen cache'leri bul
        for (const [key, value] of cacheRef.current.entries()) {
            if (now - value.timestamp >= CACHE_TTL) {
                entriesToDelete.push(key)
            }
        }
        
        // Tümünü sil
        entriesToDelete.forEach(key => cacheRef.current.delete(key))
        
        // Cache boyutu kontrolü - sadece gerektiğinde
        if (cacheRef.current.size >= MAX_CACHE_SIZE) {
            const sortedEntries = Array.from(cacheRef.current.entries())
                .sort((a, b) => a[1].timestamp - b[1].timestamp)
            
            const overflow = sortedEntries.length - MAX_CACHE_SIZE
            for (let i = 0; i < overflow; i++) {
                cacheRef.current.delete(sortedEntries[i][0])
            }
        }
    }, [])

    const performSearch = useCallback(
        async (searchQuery: string) => {
            if (!isSearchingRef.current) {
                setIsLoading(false)
                return
            }
            
            const cacheKey = searchQuery.toLowerCase().trim()
            
            // Cache kontrolü
            const cached = cacheRef.current.get(cacheKey)
            if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
                if (!isSearchingRef.current) {
                    setIsLoading(false)
                    return
                }
                setSuggestions(cached.data)
                if (!isInitialLoad() && autoOpenDropdown) {
                    setShowSuggestions(true)
                }
                setSelectedIndex(-1)
                setIsLoading(false)
                return
            }

            // Pending request kontrolü
            const pendingRequest = pendingRequestsRef.current.get(cacheKey)
            if (pendingRequest) {
                try {
                    const results = await pendingRequest
                    if (!isSearchingRef.current) {
                        setIsLoading(false)
                        return
                    }
                    setSuggestions(results)
                    if (!isInitialLoad() && autoOpenDropdown) {
                        setShowSuggestions(true)
                    }
                    setSelectedIndex(-1)
                    setIsLoading(false)
                    return
                } catch (err) {
                    // Continue on error
                }
            }

            // Önceki request'i iptal et
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }

            abortControllerRef.current = new AbortController()
            setIsLoading(true)
            setError(null)

            // Yeni arama promise'i
            const searchPromise = (async () => {
                try {
                    const results = await getSuggestions(searchQuery)
                    
                    // Cache'e ekle
                    cacheRef.current.set(cacheKey, {
                        data: results,
                        timestamp: Date.now(),
                    })
                    
                    // Cache temizleme (sadece gerektiğinde)
                    if (cacheRef.current.size >= MAX_CACHE_SIZE) {
                        cleanupCache()
                    }
                    
                    pendingRequestsRef.current.delete(cacheKey)
                    return results
                } catch (err: unknown) {
                    pendingRequestsRef.current.delete(cacheKey)
                    throw err
                }
            })()

            pendingRequestsRef.current.set(cacheKey, searchPromise)

            try {
                const results = await searchPromise
                
                if (!isSearchingRef.current) {
                    return
                }
                
                setSuggestions(results)
                if (!isInitialLoad() && autoOpenDropdown) {
                    setShowSuggestions(true)
                }
                setSelectedIndex(-1)
                setError(null)
            } catch (err: unknown) {
                if (err instanceof Error && err.name === 'AbortError') {
                    return
                }
                
                if (!isSearchingRef.current) {
                    return
                }
                
                let errorMessage = 'Arama yapılırken bir hata oluştu. Lütfen tekrar deneyin.'
                
                if (err instanceof Error) {
                    errorMessage = err.message
                }
                
                setError(errorMessage)
                setSuggestions([])
                if (!isInitialLoad() && autoOpenDropdown) {
                    setShowSuggestions(true)
                }
            } finally {
                setIsLoading(false)
            }
        },
        [getSuggestions, isInitialLoad, autoOpenDropdown, cleanupCache]
    )

    useEffect(() => {
        if (isInitialLoad()) {
            return
        }

        if (selectedSuggestion && query === selectedSuggestion.name) {
            stopSearch()
            return
        }

        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current)
        }

        if (trimmedQuery.length < MIN_SEARCH_LENGTH) {
            setSuggestions([])
            setError(null)
            stopSearch()
            return
        }

        isSearchingRef.current = true
        setIsSearchingState(true)

        debounceTimeoutRef.current = setTimeout(() => {
            performSearch(trimmedQuery)
        }, DEBOUNCE_DELAY)

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current)
                debounceTimeoutRef.current = null
            }
        }
    }, [trimmedQuery, performSearch, selectedSuggestion, isInitialLoad, stopSearch])

    useEffect(() => {
        if (isInitialLoad() && value && value !== query) {
            resetToValue(value)
            return
        }

        if (selectedSuggestion && value === selectedSuggestion.name) {
            updatePreviousValue(value)
            return
        }
        
        if (value === query) {
            updatePreviousValue(value)
            return
        }
        
        if (!value) {
            resetToValue('')
            return
        }
        
        if (previousValueRef.current === value) {
            return
        }
        
        resetToValue(value)
    }, [value, query, selectedSuggestion, isInitialLoad, resetToValue, updatePreviousValue])

    // Cache temizleme interval'i - bellek sızıntısını önlemek için
    useEffect(() => {
        const cleanupInterval = setInterval(() => {
            cleanupCache()
        }, CACHE_TTL) // Her CACHE_TTL'de bir temizle

        return () => {
            clearInterval(cleanupInterval)
        }
    }, [cleanupCache])

    const updateDropdownPosition = useCallback(() => {
        const row = inputRowRef.current
        if (!row) return
        const rect = row.getBoundingClientRect()
        const vw = window.innerWidth
        const vh = window.innerHeight
        const isMobile = vw < 640
        const margin = isMobile ? 12 : 8
        // Mobilde neredeyse tam genişlik — dar input’a yapışık dar liste okunmuyor
        const width = isMobile
            ? Math.max(280, vw - 2 * margin)
            : Math.min(800, Math.max(rect.width, Math.min(600, vw - 2 * margin)))
        let left = isMobile ? margin : rect.left
        if (!isMobile) {
            if (left + width > vw - margin) {
                left = Math.max(margin, vw - width - margin)
            }
            if (left < margin) {
                left = margin
            }
        }
        const maxHeight = Math.max(200, Math.min(isMobile ? 360 : 500, vh - rect.bottom - margin * 2))
        setDropdownPos({
            top: rect.bottom + margin,
            left,
            width,
            maxHeight,
        })
    }, [])

    // Click outside handler (portal dahil)
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node
            if (containerRef.current?.contains(target)) return
            if (dropdownPanelRef.current?.contains(target)) return
            setShowSuggestions(false)
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Cleanup on unmount - memory leak önleme
    useEffect(() => {
        return () => {
            // Abort pending requests
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
                abortControllerRef.current = null
            }
            
            // Clear pending requests
            pendingRequestsRef.current.clear()
            
            // Clear debounce timeout
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current)
                debounceTimeoutRef.current = null
            }
        }
    }, [])

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showSuggestions || suggestions.length === 0) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') {
                e.preventDefault()
                // SidebarFilters için: sadece autoOpenDropdown true ise aç
                if (trimmedQuery.length >= MIN_SEARCH_LENGTH && (autoOpenDropdown || isSearchingRef.current)) {
                    setShowSuggestions(true)
                }
            }
            return
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev))
                break
            case 'ArrowUp':
                e.preventDefault()
                setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1))
                break
            case 'Enter':
                e.preventDefault()
                if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
                    handleSelect(suggestions[selectedIndex])
                }
                break
            case 'Escape':
                stopSearch()
                setSelectedIndex(-1)
                break
        }
    }

    const handleSelect = (suggestion: Suggestion) => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current)
            debounceTimeoutRef.current = null
        }
        
        isInitialLoadRef.current = false
        
        stopSearch()
        setSelectedIndex(-1)

        // Şehir seçilse bile aynı isimde kategori varsa kategoriye yükselt (doğru page slug)
        let resolved = suggestion
        if (suggestion.type === 'station_city') {
            const cityKey = suggestion.name.trim().toLowerCase()
            const matchingCat = categories.find((c) => {
                const n = c.name.trim().toLowerCase()
                const s = (c.slug || '').trim().toLowerCase()
                return n === cityKey || n.startsWith(cityKey) || s.includes(cityKey.replace(/\s+/g, '-'))
            })
            if (matchingCat) {
                resolved = matchingCat
            }
        }
        
        setSelectedSuggestion(resolved)
        setQuery(resolved.name)
        updatePreviousValue(resolved.name)
        
        inputRef.current?.blur()
        
        onChange?.(resolved.name)
        onSelect?.(resolved)
        
        setTimeout(() => {
            closeDropdown()
        }, 0)
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value
        const hadSelection = selectedSuggestion != null
        setQuery(newValue)
        setSelectedSuggestion(null)
        // Yazınca parent’taki category/product seçimini temizle — yoksa eski slug ile yanlış yönlenir
        if (hadSelection) {
            onSelect?.(null)
        }
        
        isInitialLoadRef.current = false
        
        if (newValue.trim().length >= MIN_SEARCH_LENGTH) {
            isSearchingRef.current = true
            setIsSearchingState(true)
        } else {
            stopSearch()
        }
        
        // SidebarFilters için dropdown'ı otomatik açma, sadece focus'ta aç
        if (!autoOpenDropdown) {
            setShowSuggestions(false)
        }
        
        onChange?.(newValue)
    }

    const handleFocus = () => {
        if (isInitialLoad()) {
            return
        }
        
        if (selectedSuggestion && query === selectedSuggestion.name) {
            return
        }
        
        // SidebarFilters için: sadece focus'ta ve suggestions varsa aç (yazarken otomatik açılmasın)
        // MainSearchBar için: autoOpenDropdown true ise normal davranış
        if (query.length >= MIN_SEARCH_LENGTH) {
            if (autoOpenDropdown) {
                // MainSearchBar: suggestions varsa aç
                if (isSearchingRef.current && suggestions.length > 0) {
                    setShowSuggestions(true)
                }
            } else {
                // SidebarFilters: sadece focus'ta ve suggestions varsa aç (yazarken otomatik açılmasın)
                // handleInputChange'de zaten setShowSuggestions(false) yapıyoruz
                if (suggestions.length > 0) {
                    setShowSuggestions(true)
                }
            }
        }
    }

    const categories = useMemo(() => {
        return suggestions.filter(s => s.type === 'category')
    }, [suggestions])

    const products = useMemo(() => {
        return suggestions.filter(s => s.type === 'product')
    }, [suggestions])

    const cities = useMemo(() => {
        return suggestions.filter(s => s.type === 'station_city')
    }, [suggestions])

    const hasResults = suggestions.length > 0
    
    // showDropdown hesaplaması - memoize edilmiş ve autoOpenDropdown kontrolü eklendi
    const showDropdown = useMemo(() => {
        if (isInitialLoad()) return false
        if (!showSuggestions) return false
        if (selectedSuggestion) return false
        
        // SidebarFilters için: sadece focus'ta açık olmalı
        if (!autoOpenDropdown && !isSearchingRef.current) {
            return false
        }
        
        return (isLoading || hasResults || error || trimmedQuery.length >= MIN_SEARCH_LENGTH)
    }, [isInitialLoad, showSuggestions, selectedSuggestion, autoOpenDropdown, isSearchingRef, isLoading, hasResults, error, trimmedQuery])

    useLayoutEffect(() => {
        if (!showDropdown) {
            return
        }
        updateDropdownPosition()
        window.addEventListener('scroll', updateDropdownPosition, true)
        window.addEventListener('resize', updateDropdownPosition)
        return () => {
            window.removeEventListener('scroll', updateDropdownPosition, true)
            window.removeEventListener('resize', updateDropdownPosition)
        }
    }, [showDropdown, updateDropdownPosition])
    
    const computedPlaceholder = useMemo(() => {
        if (placeholder) return placeholder
        const defaultPlaceholder = getVariable('autocomplete_search_placeholder', `Search ${label.toLowerCase()}...`)
        return defaultPlaceholder.replace(/\{label\}/g, label).replace(/map-pin\s*/gi, '').trim()
    }, [placeholder, getVariable, label])

    const selectedExternalId = selectedSuggestion?.product_id?.trim() || ''

    return (
        <div ref={containerRef} className="relative w-full min-w-0">
            {label && (
                <label htmlFor={`autocomplete-${fieldKey}`} className="mb-1 block text-sm font-medium text-alsero-dark-gray sm:text-lg">
                    {label}
                    {isRequired && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}
            <div className="relative w-full min-w-0">
                <div
                    ref={inputRowRef}
                    className="relative flex min-h-[52px] sm:min-h-[60px] w-full min-w-0 flex-row items-center justify-between rounded-xl sm:rounded-lg border border-gray-300 bg-white px-3 sm:px-6 py-2.5 sm:py-3 shadow-sm"
                >
                    <div className="flex flex-row gap-2 items-center w-full min-w-0">
                        {icon && <div className="flex-shrink-0 opacity-80">{icon}</div>}
                        {selectedExternalId && (
                            <span
                                className="flex-shrink-0 max-w-[40%] truncate rounded-md bg-alsero-first/10 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-alsero-first"
                                title={selectedExternalId}
                            >
                                {selectedExternalId}
                            </span>
                        )}
                        <input
                            ref={inputRef}
                            id={`autocomplete-${fieldKey}`}
                            name={`alsero-destination-${fieldKey}`}
                            type="text"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            data-lpignore="true"
                            data-1p-ignore
                            data-form-type="other"
                            value={displayValue}
                            onChange={handleInputChange}
                            onFocus={handleFocus}
                            onKeyDown={handleKeyDown}
                            placeholder={computedPlaceholder}
                            className="w-full min-w-0 text-alsero-dark-gray text-base sm:text-lg bg-transparent border-none outline-none placeholder:text-alsero-light-gray placeholder:opacity-60"
                            aria-autocomplete="list"
                            aria-expanded={showSuggestions}
                            aria-controls={`suggestions-${fieldKey}`}
                            aria-activedescendant={
                                selectedIndex >= 0 ? `suggestion-${fieldKey}-${selectedIndex}` : undefined
                            }
                        />
                        {(isLoading || externalLoading) && (
                            <div className="flex-shrink-0">
                                <div className="w-5 h-5 border-2 border-alsero-dark-gray border-t-transparent rounded-full animate-spin" />
                            </div>
                        )}
                        {selectedSuggestion && !isLoading && !externalLoading && (
                            <button
                                type="button"
                                onClick={() => {
                                    resetToValue('')
                                    onChange?.('')
                                    onSelect?.(null)
                                }}
                                className="flex-shrink-0 text-alsero-light-gray hover:text-alsero-dark-gray transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center -mr-1"
                                aria-label="Clear selection"
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                            </button>
                        )}
                    </div>
                </div>

                {/* Öneri listesi — body portal: üst katman overflow scroll kesmez */}
                {typeof document !== 'undefined' &&
                    createPortal(
                        <AnimatePresence>
                            {showDropdown && dropdownPos && (
                            <motion.div
                                ref={dropdownPanelRef}
                                id={`suggestions-${fieldKey}`}
                                key={`suggestions-portal-${fieldKey}`}
                                initial={
                                    prefersReduced
                                        ? { opacity: 0 }
                                        : { opacity: 0, y: -6, scaleY: 0.98, transformOrigin: 'top center' }
                                }
                                animate={
                                    prefersReduced
                                        ? { opacity: 1 }
                                        : { opacity: 1, y: 0, scaleY: 1 }
                                }
                                exit={
                                    prefersReduced
                                        ? { opacity: 0 }
                                        : { opacity: 0, y: -4, scaleY: 0.985 }
                                }
                                transition={{
                                    duration: prefersReduced ? 0.001 : 0.18,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                className="overflow-y-auto rounded-xl bg-white shadow-2xl"
                                style={{
                                    position: 'fixed',
                                    top: dropdownPos.top,
                                    left: dropdownPos.left,
                                    width: dropdownPos.width,
                                    maxHeight: dropdownPos.maxHeight,
                                    zIndex: 10050,
                                }}
                                role="listbox"
                            >
                            {/* Loading State */}
                            {isLoading && (
                                <div className="p-8 text-center">
                                    <div className="w-8 h-8 border-3 border-alsero-first border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                                    <p className="text-alsero-light-gray text-sm">
                                        {getVariable('autocomplete_loading', 'Searching...')}
                                    </p>
                                </div>
                            )}

                            {/* Error State */}
                            {error && !isLoading && (
                                <div className="p-8 text-center">
                                    <div className="flex flex-col items-center gap-3">
                                        <svg 
                                            className="w-12 h-12 text-red-500" 
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                        >
                                            <path 
                                                strokeLinecap="round" 
                                                strokeLinejoin="round" 
                                                strokeWidth={2} 
                                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                                            />
                                        </svg>
                                        <div className="flex flex-col gap-1">
                                            <p className="text-red-600 font-medium text-sm">
                                                {getVariable('autocomplete_error_title', 'An error occurred')}
                                            </p>
                                            <p className="text-alsero-light-gray text-xs max-w-xs">
                                                {error}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Minimum Character Warning */}
                            {!isLoading &&
                                !error &&
                                trimmedQuery.length > 0 &&
                                trimmedQuery.length < MIN_SEARCH_LENGTH && (
                                    <div className="p-8 text-center">
                                        <p className="text-alsero-light-gray text-sm">
                                            {getVariable('autocomplete_min_chars', `Enter at least ${MIN_SEARCH_LENGTH} characters`)}
                                        </p>
                                    </div>
                                )}

                            {/* No Results */}
                            {!isLoading &&
                                !error &&
                                trimmedQuery.length >= MIN_SEARCH_LENGTH &&
                                !hasResults && (
                                    <div className="p-8 text-center">
                                        <p className="text-alsero-light-gray text-sm">
                                            {getVariable('autocomplete_no_results', 'No results found')}
                                        </p>
                                    </div>
                                )}

                            {/* Results */}
                            {!isLoading && !error && hasResults && (
                                <div className="py-2">
                                    {/* Categories */}
                                    {categories.length > 0 && (
                                        <div className="px-2 sm:px-4 py-2">
                                            <h3 className="text-xs font-semibold text-alsero-light-gray uppercase mb-2 px-2 sm:px-0">
                                                {getVariable('autocomplete_categories', 'Categories')}
                                            </h3>
                                            {categories.map((cat, idx) => {
                                                const globalIndex = idx
                                                const isSelected = selectedIndex === globalIndex

                                                return (
                                                    <motion.div
                                                        key={`cat-${cat.id}`}
                                                        initial={
                                                            prefersReduced
                                                                ? { opacity: 0 }
                                                                : { opacity: 0, y: -6 }
                                                        }
                                                        animate={
                                                            prefersReduced
                                                                ? { opacity: 1 }
                                                                : { opacity: 1, y: 0 }
                                                        }
                                                        transition={{
                                                            duration: prefersReduced ? 0.001 : 0.12,
                                                            ease: [0.22, 1, 0.36, 1],
                                                            delay: idx * 0.02,
                                                        }}
                                                    >
                                                        <div
                                                            id={`suggestion-${fieldKey}-${globalIndex}`}
                                                            onClick={() => handleSelect(cat)}
                                                            className={`
                                                                flex flex-row items-center gap-3 sm:gap-4 px-2 sm:px-4 py-3 rounded-lg transition-all duration-200 cursor-pointer min-h-[52px]
                                                                ${isSelected
                                                                    ? 'bg-alsero-first/10 text-alsero-first border-l-4 border-alsero-first'
                                                                    : 'text-alsero-dark-gray hover:bg-gray-50 hover:text-alsero-first'
                                                                }
                                                            `}
                                                            role="option"
                                                            aria-selected={isSelected}
                                                        >
                                                            {cat.image_url ? (
                                                                <div className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-lg overflow-hidden bg-gray-100">
                                                                    <img
                                                                        src={transformMediaUrl(cat.image_url, null) || cat.image_url}
                                                                        alt={cat.name}
                                                                        className="w-full h-full object-cover"
                                                                        loading="lazy"
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-alsero-first/20 to-alsero-dark-gray/10 flex items-center justify-center">
                                                                    <svg className="w-6 h-6 text-alsero-first" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                                                    </svg>
                                                                </div>
                                                            )}
                                                            <span className="font-medium flex-1">
                                                                {highlightText(cat.name, trimmedQuery)}
                                                            </span>
                                                        </div>
                                                    </motion.div>
                                                )
                                            })}
                                        </div>
                                    )}

                                    {/* Cities (station_city – ara duraklar / şehir) */}
                                    {cities.length > 0 && (
                                        <div className={`px-2 sm:px-4 py-2 ${(categories.length > 0 || products.length > 0) ? 'border-t border-gray-100' : ''}`}>
                                            <h3 className="text-xs font-semibold text-alsero-light-gray uppercase mb-2 px-2 sm:px-0">
                                                {getVariable('autocomplete_cities', 'Cities')}
                                            </h3>
                                            {cities.map((city, idx) => {
                                                const globalIndex = categories.length + idx
                                                const isSelected = selectedIndex === globalIndex

                                                return (
                                                    <motion.div
                                                        key={`city-${city.id}-${city.name}`}
                                                        initial={
                                                            prefersReduced
                                                                ? { opacity: 0 }
                                                                : { opacity: 0, y: -6 }
                                                        }
                                                        animate={
                                                            prefersReduced
                                                                ? { opacity: 1 }
                                                                : { opacity: 1, y: 0 }
                                                        }
                                                        transition={{
                                                            duration: prefersReduced ? 0.001 : 0.12,
                                                            ease: [0.22, 1, 0.36, 1],
                                                            delay: idx * 0.02,
                                                        }}
                                                    >
                                                        <div
                                                            id={`suggestion-${fieldKey}-${globalIndex}`}
                                                            onClick={() => handleSelect(city)}
                                                            className={`
                                                                flex flex-row items-center gap-3 sm:gap-4 px-2 sm:px-4 py-3 rounded-lg transition-all duration-200 cursor-pointer min-h-[52px]
                                                                ${isSelected
                                                                    ? 'bg-alsero-first/10 text-alsero-first border-l-4 border-alsero-first'
                                                                    : 'text-alsero-dark-gray hover:bg-gray-50 hover:text-alsero-first'
                                                                }
                                                            `}
                                                            role="option"
                                                            aria-selected={isSelected}
                                                        >
                                                            <div className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br from-alsero-first/20 to-alsero-dark-gray/10 flex items-center justify-center">
                                                                <svg className="w-6 h-6 text-alsero-first" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                </svg>
                                                            </div>
                                                            <span className="font-medium flex-1 text-sm sm:text-base">
                                                                {highlightText(city.name, trimmedQuery)}
                                                            </span>
                                                        </div>
                                                    </motion.div>
                                                )
                                            })}
                                        </div>
                                    )}

                                    {/* Products */}
                                    {products.length > 0 && (
                                        <div className={`px-2 sm:px-4 py-2 ${categories.length > 0 || cities.length > 0 ? 'border-t border-gray-100' : ''}`}>
                                            <h3 className="text-xs font-semibold text-alsero-light-gray uppercase mb-2 px-2 sm:px-0">
                                                {getVariable('autocomplete_products', 'Products')}
                                            </h3>
                                            {products.map((product, idx) => {
                                                const globalIndex = categories.length + cities.length + idx
                                                const isSelected = selectedIndex === globalIndex
                                                const externalId = product.product_id?.trim() || ''

                                                return (
                                                    <motion.div
                                                        key={`product-${product.id}`}
                                                        initial={
                                                            prefersReduced
                                                                ? { opacity: 0 }
                                                                : { opacity: 0, y: -6 }
                                                        }
                                                        animate={
                                                            prefersReduced
                                                                ? { opacity: 1 }
                                                                : { opacity: 1, y: 0 }
                                                        }
                                                        transition={{
                                                            duration: prefersReduced ? 0.001 : 0.12,
                                                            ease: [0.22, 1, 0.36, 1],
                                                            delay: idx * 0.02,
                                                        }}
                                                    >
                                                        <div
                                                            id={`suggestion-${fieldKey}-${globalIndex}`}
                                                            onClick={() => handleSelect(product)}
                                                            className={`
                                                                flex flex-row items-center gap-3 sm:gap-4 px-2 sm:px-4 py-3 rounded-lg transition-all duration-200 cursor-pointer min-h-[56px]
                                                                ${isSelected
                                                                    ? 'bg-alsero-first/10 text-alsero-first border-l-4 border-alsero-first'
                                                                    : 'text-alsero-dark-gray hover:bg-gray-50 hover:text-alsero-first'
                                                                }
                                                            `}
                                                            role="option"
                                                            aria-selected={isSelected}
                                                        >
                                                            {product.image_url ? (
                                                                <div className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-lg overflow-hidden bg-gray-100">
                                                                    <img
                                                                        src={transformMediaUrl(product.image_url, null) || product.image_url}
                                                                        alt={product.name}
                                                                        className="w-full h-full object-cover"
                                                                        loading="lazy"
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <div className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br from-alsero-first/20 to-alsero-dark-gray/10 flex items-center justify-center">
                                                                    <svg className="w-6 h-6 text-alsero-first" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                                    </svg>
                                                                </div>
                                                            )}
                                                            <div className="flex-1 min-w-0">
                                                                {externalId && (
                                                                    <span className="inline-flex mb-1 max-w-full truncate rounded-md bg-alsero-first/10 px-2 py-0.5 text-[11px] sm:text-xs font-semibold uppercase tracking-wide text-alsero-first">
                                                                        {highlightText(externalId, trimmedQuery)}
                                                                    </span>
                                                                )}
                                                                <span className="font-medium block text-sm sm:text-base leading-snug line-clamp-2">
                                                                    {highlightText(product.name, trimmedQuery)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                )
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </motion.div>
                            )}
                        </AnimatePresence>,
                        document.body
                    )}
            </div>
        </div>
    )
}
